import { useContext, useMemo } from "react";

import { IEvalDetail, IFbClient, ReasonKinds } from "@featbit/js-client-sdk";

import { EvaluateOptions, FeatureFlagContext, FlagEvaluationResult, FlagValue } from "utils";

const defaultResolveEnabled = <T extends FlagValue>(value: T): boolean => {
  if (typeof value === "boolean") return value === true;
  return value !== undefined && value !== null && (value as unknown) !== false;
};

// E2E test seam: deterministic flag overrides injected on `window` (e.g. by Playwright via addInitScript).
// Never set in production — the property simply does not exist — so this is a no-op outside tests.
const getForcedFlag = <T extends FlagValue>(flagKey: string): T | undefined => {
  if (typeof window === "undefined") return undefined;
  const overrides = (window as Window & { __E2E_FEATURE_FLAGS__?: Record<string, FlagValue> }).__E2E_FEATURE_FLAGS__;
  const value = overrides?.[flagKey];
  return value === undefined ? undefined : (value as T);
};

const evalDetail = <T extends FlagValue>(client: IFbClient, flagKey: string, defaultValue: T): IEvalDetail<T> => {
  if (typeof defaultValue === "boolean") {
    return client.boolVariationDetail(flagKey, defaultValue) as unknown as IEvalDetail<T>;
  }
  if (typeof defaultValue === "string") {
    return client.stringVariationDetail(flagKey, defaultValue) as unknown as IEvalDetail<T>;
  }
  if (typeof defaultValue === "number") {
    return client.numberVariationDetail(flagKey, defaultValue) as unknown as IEvalDetail<T>;
  }
  return client.jsonVariationDetail(flagKey, defaultValue) as unknown as IEvalDetail<T>;
};

/**
 * Evaluate a FeatBit feature flag inside a React component.
 *
 * Returns a stable object describing the current evaluation:
 * - `isEnabled` — `true` when the flag has a truthy, non-false value. Use this for on/off gates.
 * - `value`    — the raw variation value. Its type is driven by `defaultValue` (bool / string / number / json).
 * - `kind`     — a `ReasonKinds` enum describing *why* this value was returned (e.g. `ClientNotReady`, `Off`, `TargetMatch`, `Fallthrough`, `Error`).
 * - `reason`   — optional human-readable reason string from the SDK.
 * - `flagKey`  — echoes back the flag key for convenience.
 *
 * ## Options
 *
 * - `project`      — which FeatBit project the flag lives in. Defaults to `"client"`. Use `"common"` for flags shared across multiple apps.
 * - `defaultValue`   — returned when the SDK is not yet ready, the flag does not exist, or evaluation errors. Its TypeScript type drives which `*VariationDetail` method the SDK calls:
 *   - `boolean` → `boolVariationDetail`
 *   - `string`  → `stringVariationDetail`
 *   - `number`  → `numberVariationDetail`
 *   - object    → `jsonVariationDetail`
 * - `resolveEnabled` — optional predicate `(value) => boolean` that derives the final `isEnabled` from the raw variation value. Use when the flag value is not a plain boolean (e.g. a JSON object like `{ enabled: true, variant: "v2" }`). Defaults to: `true` for boolean `true`, and "not null / not undefined / not false" for other types. The predicate runs against the resolved value in **both** the ready and not-ready branches, so `defaultValue` is interpreted consistently from the first render.
 *
 * ## Behavior
 *
 * The hook re-evaluates automatically whenever the SDK pushes a flag update, so
 * components using it will re-render with the new value without any manual
 * subscription. While the SDK is still initializing — or when feature flags are
 * disabled via env vars — the hook evaluates `defaultValue` through `resolveEnabled`
 * (or the built-in truthy check) and returns `{ isEnabled, value: defaultValue, kind: ClientNotReady }`,
 * so callers can render a sensible fallback from the first render.
 *
 * ## Examples
 *
 * Boolean flag — simplest form:
 * ```tsx
 * const { isEnabled } = useFeatureFlag(FEATURE_FLAGS.DOC_SCAN_FOR_FARMERS, {
 *   project: "common",
 *   defaultValue: false,
 * });
 *
 * if (!isEnabled) return null;
 * return <DocumentScanPage />;
 * ```
 *
 * JSON flag with a custom predicate — mirrors the server-side middleware contract:
 * ```tsx
 * const { isEnabled, value } = useFeatureFlag<{ enabled: boolean; maxUploads: number }>(
 *   FEATURE_FLAGS.DOC_SCAN_FOR_FARMERS,
 *   {
 *     project: "common",
 *     defaultValue: { enabled: false, maxUploads: 0 },
 *     resolveEnabled: (v) => v?.enabled === true,
 *   },
 * );
 * ```
 */
export const useFeatureFlag = <T extends FlagValue = boolean>(flagKey: string, options?: EvaluateOptions<T>): FlagEvaluationResult<T> => {
  const { project = "client", defaultValue, resolveEnabled } = options ?? {};
  const resolvedDefault = (defaultValue ?? false) as T;
  const resolve = resolveEnabled ?? defaultResolveEnabled;
  const { getRawClient, ready, version } = useContext(FeatureFlagContext);

  return useMemo<FlagEvaluationResult<T>>(() => {
    const forced = getForcedFlag<T>(flagKey);
    if (forced !== undefined) {
      return { isEnabled: resolve(forced), value: forced, kind: ReasonKinds.Match, flagKey, isForced: true };
    }

    const client = getRawClient(project);
    if (!client || !ready[project]) {
      return { isEnabled: resolve(resolvedDefault), value: resolvedDefault, kind: ReasonKinds.ClientNotReady, flagKey };
    }

    const detail = evalDetail(client, flagKey, resolvedDefault);
    const hasValue = detail.value !== undefined && detail.value !== null;
    const effectiveValue = hasValue ? (detail.value as T) : resolvedDefault;
    const isEnabled = resolve(effectiveValue);

    return {
      isEnabled,
      value: effectiveValue,
      reason: detail.reason,
      kind: detail.kind,
      flagKey: detail.flagKey,
    };
    // version drives re-evaluation on flag updates from the SDK
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flagKey, project, resolvedDefault, resolve, ready, version]);
};
